import { AfterViewInit, Component, ElementRef, OnDestroy, input, viewChild, signal } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Subscription } from "rxjs";
import { Map as MapLibreMap, Marker, NavigationControl } from "maplibre-gl";
import { DecimalPipe } from "@angular/common";
import { Bind } from "primeng/bind";
import { ButtonDirective } from "primeng/button";

import { TPipe } from "../../i18n/t.pipe";
@Component({
    standalone: true,
    selector: "app-address-map",
    templateUrl: "./address-map.component.html",
    styles: [`
    .amap-canvas { width: 100%; border: 1px solid #d1d5db; border-radius: 6px; }
    .amap-bar { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; margin-top: 8px; }
    .amap-bar small { color: #6b7280; }
  `],
    imports: [TPipe, Bind, ButtonDirective, DecimalPipe]
})
export class AddressMapComponent implements AfterViewInit, OnDestroy {
  readonly form = input.required<FormGroup>();
  readonly height = input(320);

  readonly mapStyle = input("https://tiles.openfreemap.org/styles/liberty");

  readonly startCenter = input<[
    number,
    number
]>([12.5, 42.5]);

  readonly canvas = viewChild.required<ElementRef<HTMLDivElement>>("canvas");

  readonly busy = signal(false);
  readonly message = signal("");

  private readonly map = signal<MapLibreMap>(undefined);
  private readonly marker = signal<Marker>(undefined);
  private readonly resizer = signal<ResizeObserver>(undefined);
  private subs = new Subscription();
  private readonly own = signal(false);

  ngAfterViewInit(): void {
    this.map.set(new MapLibreMap({
      container: this.canvas().nativeElement,
      style: this.mapStyle(),
      center: this.startCenter(),
      zoom: 4.5,
      cooperativeGestures: true,
    }));
    this.map().addControl(new NavigationControl({ showCompass: false }), "top-right");
    this.map().on("click", (e) => this.setPoint(e.lngLat.lat, e.lngLat.lng));

    if (typeof ResizeObserver !== "undefined") {
      this.resizer.set(new ResizeObserver(() => this.map()?.resize()));
      this.resizer().observe(this.canvas().nativeElement);
    }

    ["lat", "lng"].forEach((name) => {
      const control = this.form()?.get(name);
      if (control) this.subs.add(control.valueChanges.subscribe(() => this.sync(false)));
    });
    this.map().on("load", () => this.sync(true));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.resizer()?.disconnect();
    this.map()?.remove();
  }

  get point(): { lat: number; lng: number } | null {
    const form = this.form();
    const lat = parseFloat(form?.get("lat")?.value);
    const lng = parseFloat(form?.get("lng")?.value);
    return isNaN(lat) || isNaN(lng) ? null : { lat, lng };
  }

  get osmLink(): string | null {
    const p = this.point;
    return p ? `https://www.openstreetmap.org/?mlat=${p.lat}&mlon=${p.lng}#map=17/${p.lat}/${p.lng}` : null;
  }

  private sync(first: boolean): void {
    if (!this.map()) return;
    const p = this.point;
    if (!p) {
      this.marker()?.remove();
      this.marker.set(undefined);
      return;
    }
    if (!this.marker()) {
      this.marker.set(new Marker({ color: "#dc2626", draggable: true }).setLngLat([p.lng, p.lat]).addTo(this.map()));
      this.marker().on("dragend", () => {
        const at = this.marker()!.getLngLat();
        this.setPoint(at.lat, at.lng);
      });
    } else {
      this.marker().setLngLat([p.lng, p.lat]);
    }
    if (!this.own()) {
      this.map().flyTo({ center: [p.lng, p.lat], zoom: 16, animate: !first });
    }
  }

  private setPoint(lat: number, lng: number): void {
    this.message.set("");
    this.own.set(true);
    this.form().patchValue({ lat: lat.toFixed(7), lng: lng.toFixed(7) });
    this.form().markAsDirty();
    this.own.set(false);
    this.sync(false);
  }

  async fillFromPoint(): Promise<void> {
    const p = this.point;
    if (!p || this.busy()) return;
    this.busy.set(true);
    this.message.set("");
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&zoom=18&addressdetails=1&accept-language=it&lat=${p.lat}&lon=${p.lng}`,
        { headers: { Accept: "application/json" } },
      );
      if (!r.ok) throw new Error("reverse");
      const data = await r.json();
      const a = data.address;
      if (!a) {
        this.message.set("Nessun indirizzo trovato in questo punto.");
        return;
      }
      const road = a.road || a.pedestrian || a.footway || a.path || a.square || "";
      const iso: string = a["ISO3166-2-lvl6"] || "";
      const found: { [name: string]: string } = {
        address: road ? road + (a.house_number ? " " + a.house_number : "") : "",
        zip: a.postcode || "",
        city: a.city || a.town || a.village || a.municipality || a.hamlet || "",
        province: iso.includes("-") ? iso.split("-")[1] : a.county || "",
        region: a.state || a.region || (a["ISO3166-2-lvl4"] ? a.city || a.town || "" : ""),
        country: a.country || "",
      };

      const patch: { [name: string]: string } = {};
      Object.keys(found).forEach((name) => {
        if (!this.form().get(name)) return;
        if (name === "address" && !found[name]) return;
        patch[name] = found[name];
      });
      this.form().patchValue(patch);
      this.form().markAsDirty();
    } catch {
      this.message.set("Servizio indirizzi non raggiungibile. Riprova tra poco.");
    } finally {
      this.busy.set(false);
    }
  }
}
